import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Moon, Star, BookOpen, Sparkles, Heart } from "lucide-react";
import { BackButton } from "./ui/BackButton";
import { getDayDua, getPeriodForDay, isOddNight, getLaylatulQadrNightFocus } from "@/data/dailyDuas";

import { NameCard } from "./NameCard";
import { ReflectionWorksheet } from "./ReflectionWorksheet";
import { useProfile } from "@/hooks/useProfile";

interface GoodDeed {
  id: string;
  text: string;
  day: number;
  timestamp: number;
}

interface TrackerData {
  prayers: Record<number, string[]>;
  quranProgress: Record<number, { pages: number; juz: number }>;
  fastedDays: number[];
  missedFastDays: number[];
  goodDeeds: GoodDeed[];
  dhikrCounts: Record<number, Record<string, number>>;
}

interface DailyDuaPageProps {
  currentDay: number;
  completedDays: number[];
  trackerData: TrackerData;
  onDayChange: (day: number) => void;
  onDayToggle: (day: number) => void;
  onTrackerUpdate: (updates: Partial<TrackerData>) => void;
  onBackToCover: () => void;
  onOpenLaylatulQadr: () => void;
}

export const DailyDuaPage = ({
  currentDay,
  completedDays,
  trackerData,
  onDayChange,
  onDayToggle,
  onTrackerUpdate,
  onBackToCover,
  onOpenLaylatulQadr,
}: DailyDuaPageProps) => {
  const { profile } = useProfile();
  const dua = getDayDua(currentDay);
  const period = getPeriodForDay(currentDay);
  const isSpecialNight = isOddNight(currentDay);
  const nightFocus = getLaylatulQadrNightFocus(currentDay);
  const isCompleted = completedDays.includes(currentDay);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handlePrevious = () => {
    if (currentDay > 1) onDayChange(currentDay - 1);
  };

  const handleNext = () => {
    if (currentDay < 30) onDayChange(currentDay + 1);
  };

  if (!dua || !period) return null;

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'prophetic': return 'Prophetic Dua';
      case 'quranic': return 'Quranic Dua';
      case 'recommended': return 'Recommended Dua';
      default: return 'Dua';
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      {/* Fixed Compact Header - Visible on Scroll */}
      <AnimatePresence>
        {hasScrolled && (
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-0 left-0 right-0 z-20 bg-paper border-b border-border"
          >
            <div className="max-w-3xl mx-auto px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <BackButton onClick={onBackToCover} srLabel="Return to dashboard" />
                  
                  <div className="flex items-center gap-3">
                    <p className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-1.5">
                      {isSpecialNight && <Star className="w-3 h-3" fill="currentColor" />}
                      Day {currentDay}
                    </p>
                    <span className="text-muted-foreground/30">·</span>
                    <p className="text-sm text-ink font-medium">
                      {period.name}
                    </p>
                    <p className="font-arabic text-xs text-muted-foreground" dir="rtl">
                      {period.arabicName}
                    </p>
                  </div>
                </div>

                {period.id === 3 && (
                  <button
                    onClick={onOpenLaylatulQadr}
                    className="flex items-center gap-2 text-xs bg-gold/10 text-gold px-3 py-1.5 rounded-full hover:bg-gold/20 transition-colors"
                  >
                    <Moon className="w-3 h-3" />
                    Guide
                  </button>
                )}
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* Hero Header - Visible at Top */}
      <motion.header
        className="pt-6 pb-8"
        animate={{ opacity: hasScrolled ? 0.3 : 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-6">
            <BackButton onClick={onBackToCover} label="Dashboard" srLabel="Return to dashboard" />
          </div>
          
          <div className="text-center space-y-2">
            <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center justify-center gap-2">
              {isSpecialNight && <Star className="w-3 h-3" fill="currentColor" />}
              Day {currentDay}
              {isSpecialNight && <Star className="w-3 h-3" fill="currentColor" />}
            </p>
            <h1 className="font-display text-2xl md:text-3xl text-ink tracking-tight">
              {period.name}
            </h1>
            <p className="font-arabic text-lg text-muted-foreground" dir="rtl">
              {period.arabicName}
            </p>
            
            {period.id === 3 && (
              <button
                onClick={onOpenLaylatulQadr}
                className="inline-flex items-center gap-2 text-xs bg-gold/10 text-gold px-4 py-2 rounded-full hover:bg-gold/20 transition-colors mt-4"
              >
                <Moon className="w-3 h-3" />
                Laylatul Qadr Guide
              </button>
            )}
          </div>
        </div>
      </motion.header>

      <main className="max-w-2xl mx-auto px-6">
        <motion.article
          key={currentDay}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-12"
        >
          {/* Bismillah Card Section */}
          <section className="pt-8">
            <div className="bg-primary rounded-2xl p-6 md:p-8 text-primary-foreground">
              <p className="text-xs uppercase tracking-[0.3em] text-primary-foreground/70 mb-2">
                In the Name of Allah
              </p>
              <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-2">
                Bismillah
              </h1>
              <p className="font-arabic text-lg text-primary-foreground/80" dir="rtl">
                بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
              </p>
              <p className="text-sm text-primary-foreground/60 mt-4">
                The Most Gracious, The Most Merciful
              </p>
            </div>
          </section>

          {/* Special Night Notice */}
          {isSpecialNight && nightFocus && (
            <motion.section
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-r from-purple-50 to-gold/5 rounded-sm p-6 border border-gold/20"
            >
              <div className="flex items-start gap-4">
                <div className="bg-gold/20 p-2 rounded-full">
                  <Moon className="w-5 h-5 text-gold" />
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="font-medium text-ink flex items-center gap-2">
                    Possible Laylatul Qadr
                    <Sparkles className="w-4 h-4 text-gold" />
                  </h3>
                  <p className="text-sm text-foreground/80">
                    <strong>Tonight's Focus:</strong> {nightFocus.focus}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {nightFocus.specialDua}
                  </p>
                  <p className="text-xs text-gold/80 italic mt-3">
                    {nightFocus.reminder}
                  </p>
                </div>
              </div>
            </motion.section>
          )}

          {/* Period Description */}
          <section className="bg-cream rounded-sm p-6">
            <p className="text-foreground/80 leading-relaxed text-center">
              {period.description}
            </p>
            <p className="text-sm text-gold text-center mt-4 italic">
              {period.blessing}
            </p>
          </section>

          {/* Daily Dua */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <BookOpen className="w-4 h-4 text-gold" />
              <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {getTypeLabel(dua.type)}
              </h2>
            </div>

            {/* Arabic */}
            <div className="bg-cream p-8 rounded-sm">
              <p className="font-arabic text-2xl md:text-3xl text-ink text-center leading-loose" dir="rtl">
                {dua.arabic}
              </p>
            </div>

            {/* Transliteration */}
            <p className="text-center italic text-muted-foreground">
              {dua.transliteration}
            </p>

            {/* Translation */}
            <blockquote className="text-center text-lg text-foreground/90 leading-relaxed">
              "{dua.translation}"
            </blockquote>

            <p className="text-center text-xs text-muted-foreground uppercase tracking-wider">
              — {dua.source}
            </p>

            {/* Reflection */}
            <div className="bg-gold/5 border-l-2 border-gold p-5 mt-6">
              <p className="text-sm text-foreground/80 leading-relaxed">
                {dua.reflection}
              </p>
            </div>
          </section>

          {/* Heartfelt Dua - Conversation with Allah */}
          <section className="space-y-6">
            <header className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2 text-gold">
                <Heart className="w-4 h-4" />
              </div>
              <h2 className="font-display text-2xl text-ink">
                A Conversation with Your Creator
              </h2>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {period.name}
              </p>
              <div className="divider-elegant" />
            </header>

            <div className="space-y-4 text-foreground/85 leading-loose">
              {period.heartfeltDua.map((paragraph, index) => (
                <motion.p
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>
          </section>

          {/* Names of Allah Section */}
          <section className="space-y-8">
            <header className="text-center space-y-2">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">
                Allah's Beautiful Names
              </p>
              <h2 className="font-display text-2xl text-ink">
                {period.name}
              </h2>
              <div className="divider-elegant" />
            </header>

            <div className="space-y-6">
              {period.namesOfAllah.map((name, index) => (
                <NameCard key={name.transliteration} name={name} index={index} />
              ))}
            </div>
          </section>

          {/* Day Selector */}
          <section className="bg-cream rounded-sm p-6">
            <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground text-center mb-4">
              Navigate Days
            </h2>
            <div className="flex flex-wrap justify-center gap-1.5">
              {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => {
                const dayPeriod = getPeriodForDay(day);
                const dayIsOdd = isOddNight(day);
                const dayCompleted = completedDays.includes(day);
                
                return (
                  <button
                    key={day}
                    onClick={() => onDayChange(day)}
                    className={`w-8 h-8 rounded-full text-xs font-medium transition-all flex items-center justify-center ${
                      day === currentDay
                        ? 'bg-gold text-white scale-110 shadow-md'
                        : dayCompleted
                        ? 'bg-emerald-500 text-white'
                        : dayIsOdd && dayPeriod?.id === 3
                        ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        : 'bg-background text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-center gap-4 mt-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-emerald-500" />
                Completed
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-purple-100 border border-purple-200" />
                Odd Nights
              </span>
            </div>
          </section>

          {/* Complete Day Button */}
          <div className="text-center">
            <motion.button
              onClick={() => onDayToggle(currentDay)}
              className={`inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-[0.1em] rounded-sm transition-all ${
                isCompleted
                  ? "bg-emerald-600 text-white"
                  : "border border-gold text-gold hover:bg-gold hover:text-white"
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isCompleted ? "Day Completed ✓" : "Mark Day Complete"}
            </motion.button>
          </div>

          {/* Affirmations */}
          <section className="bg-cream rounded-sm p-8 space-y-6">
            <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground text-center">
              Islamic Affirmations — {period.name}
            </h2>
            <ul className="space-y-4">
              {period.affirmations.map((affirmation, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-4 text-foreground/85"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="text-gold text-xs mt-1.5">✦</span>
                  <span>{affirmation}</span>
                </motion.li>
              ))}
            </ul>
          </section>


          {/* Reflection Questions (Read-only prompts) */}
          <section className="space-y-6">
            <header className="flex items-center gap-4">
              <div className="flex-1 h-px bg-border" />
              <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Reflection Questions — {period.name}
              </h2>
              <div className="flex-1 h-px bg-border" />
            </header>

            <div className="bg-cream/50 rounded-sm p-6 space-y-4">
              {period.reflectionQuestions.map((question, index) => (
                <div key={index} className="flex items-start gap-3">
                  <span className="text-gold font-medium">{index + 1}.</span>
                  <p className="text-foreground/85 leading-relaxed">{question}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Daily Journal (Interactive) */}
          <section className="space-y-6">
            <header className="flex items-center gap-4">
              <div className="flex-1 h-px bg-border" />
              <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Daily Journal
              </h2>
              <div className="flex-1 h-px bg-border" />
            </header>

            <ReflectionWorksheet 
              currentDay={currentDay} 
              ramadanStartDate={profile?.ramadanStartDate || undefined}
            />
          </section>

          {/* Navigation */}
          <footer className="pt-8 border-t border-border/50 flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentDay === 1}
              className="group flex items-center gap-2 text-sm uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Previous
            </button>

            <span className="text-xs text-muted-foreground">
              {currentDay} / 30
            </span>

            <button
              onClick={handleNext}
              disabled={currentDay === 30}
              className="group flex items-center gap-2 text-sm uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </footer>
        </motion.article>
      </main>
    </div>
  );
};
